import axios from "axios";
import api, { type PaginatedResponse } from "./api";

export interface ConversionMetadata {
  spz_version?: number;
  converter?: string;
  converter_version?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  error_message?: string | null;
  skipped_reason?: string;
}

export interface DigitalTwin {
  id: string;
  virtual_site_id: string;
  name: string;
  description: string | null;
  s3_bucket: string;
  s3_key: string;
  derived_s3_key: string | null;
  s3_region: string;
  format: string;
  derived_format: string | null;
  point_count: number | null;
  file_size_bytes: number | null;
  derived_file_size_bytes: number | null;
  bounds: Record<string, number> | null;
  coordinate_system: string | null;
  latitude: number | null;
  longitude: number | null;
  capture_date: string | null;
  status: "uploading" | "processing" | "ready" | "error" | "archived";
  metadata_: Record<string, unknown> | null;
  conversion_metadata: ConversionMetadata | null;
  created_at: string;
  updated_at: string;
}

export interface DigitalTwinListItem {
  id: string;
  virtual_site_id: string;
  name: string;
  description: string | null;
  status: string;
  format: string;
  derived_format: string | null;
  point_count: number | null;
  file_size_bytes: number | null;
  derived_file_size_bytes: number | null;
  latitude: number | null;
  longitude: number | null;
  capture_date: string | null;
  created_at: string;
}

export interface DigitalTwinViewURL {
  digital_twin_id: string;
  view_url: string | null;
  expires_in: number;
}

export interface DigitalTwinTag {
  id: string;
  asset_id: string;
  /** Denormalized from the assets table by the backend's hydration helper
   *  so the viewer sidebar can show the asset identifier without a second
   *  round trip. May be null if the asset row was deleted out-of-band. */
  asset_name: string | null;
  asset_code: string | null;
  point_cloud_id: string | null;
  digital_twin_id: string | null;
  tag_type: "point" | "line" | "polygon" | "area";
  geometry: Record<string, unknown>;
  label: string | null;
  description: string | null;
  color: string | null;
  opacity: number;
  created_by: string | null;
  metadata_: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export async function listDigitalTwins(params: {
  virtual_site_id: string;
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
}): Promise<PaginatedResponse<DigitalTwinListItem>> {
  const res = await api.get("/digital-twins", { params });
  return res.data;
}

export async function getDigitalTwin(id: string): Promise<DigitalTwin> {
  const res = await api.get(`/digital-twins/${id}`);
  return res.data;
}

/**
 * Set/update a twin's WGS84 georeference so it appears on the 2D map. The
 * PUT endpoint takes query params (not a JSON body). Coordinates are always
 * sent as a pair; the backend skips null values so this can only set, not
 * clear, coordinates — matching the Details modal's "Save location" action.
 */
export async function updateDigitalTwinLocation(
  id: string,
  coords: { latitude: number; longitude: number },
): Promise<DigitalTwin> {
  const res = await api.put(`/digital-twins/${id}`, null, {
    params: { latitude: coords.latitude, longitude: coords.longitude },
  });
  return res.data;
}

export interface CreateDigitalTwinPayload {
  virtual_site_id: string;
  name: string;
  description?: string;
  file: File;
  /** Optional WGS84 georeference (decimal degrees) for the 2D map. */
  latitude?: number;
  longitude?: number;
}

interface InitiateUploadResponse {
  digital_twin_id: string;
  upload_url: string | null;
  s3_key: string;
  expires_in: number;
}

/**
 * Create a digital twin via the browser→S3 direct upload flow:
 *   1. POST /digital-twins/initiate-upload  -> presigned PUT URL + dt_id
 *   2. PUT to S3 with the file body
 *   3. POST /digital-twins/{id}/complete-upload  -> finalize + start conversion
 *
 * The API never sees the file bytes, so file size is bounded by S3's 5 GB
 * single-PUT cap, not by API gateway / CloudFront body limits. If step 2 or
 * 3 fails we DELETE the dt row to clean up the orphaned 'uploading' state
 * — without this, list views would accumulate ghost rows after a flaky
 * network or a user closing the tab mid-upload.
 *
 * In dev mode the backend returns upload_url: null. We skip the PUT and go
 * straight to complete-upload (the backend's dev-mode short-circuit trusts
 * the declared size).
 */
export async function createDigitalTwin(
  payload: CreateDigitalTwinPayload,
  onUploadProgress?: (loaded: number, total: number) => void,
  options?: { signal?: AbortSignal },
): Promise<DigitalTwin> {
  const contentType = payload.file.type || "application/octet-stream";

  const initiateRes = await api.post<InitiateUploadResponse>(
    "/digital-twins/initiate-upload",
    {
      virtual_site_id: payload.virtual_site_id,
      name: payload.name,
      description: payload.description,
      filename: payload.file.name,
      file_size_bytes: payload.file.size,
      content_type: contentType,
      latitude: payload.latitude,
      longitude: payload.longitude,
    },
    { signal: options?.signal },
  );
  const { digital_twin_id, upload_url } = initiateRes.data;

  try {
    if (upload_url) {
      // PUT directly to S3 with raw axios (no auth interceptor — S3 rejects
      // requests carrying an unrelated Authorization header). The Content-Type
      // must match what was signed in initiate-upload or S3 returns
      // SignatureDoesNotMatch.
      await axios.put(upload_url, payload.file, {
        headers: { "Content-Type": contentType },
        onUploadProgress: (e) => {
          if (onUploadProgress && e.total) onUploadProgress(e.loaded, e.total);
        },
        signal: options?.signal,
        // Allow the upload to run as long as the presigned URL is valid.
        // Axios defaults to no timeout for PUT, but be explicit since slow
        // mobile connections + GB-scale files can otherwise time out at
        // the browser's default.
        timeout: 0,
      });
    }

    const completeRes = await api.post<DigitalTwin>(
      `/digital-twins/${digital_twin_id}/complete-upload`,
      null,
      { signal: options?.signal },
    );
    return completeRes.data;
  } catch (err) {
    // Best-effort cleanup of the half-created row. If the delete itself
    // fails we surface the original error, not the delete error.
    try {
      await api.delete(`/digital-twins/${digital_twin_id}`);
    } catch {
      // swallow — abort/network errors during cleanup are noise
    }
    throw err;
  }
}

export async function deleteDigitalTwin(id: string): Promise<void> {
  await api.delete(`/digital-twins/${id}`);
}

export async function getViewUrl(id: string): Promise<DigitalTwinViewURL> {
  const res = await api.get(`/digital-twins/${id}/view-url`);
  return res.data;
}

export async function reprocessDigitalTwin(id: string): Promise<DigitalTwin> {
  const res = await api.post(`/digital-twins/${id}/reprocess`);
  return res.data;
}

export async function listTagsForDigitalTwin(
  digitalTwinId: string,
): Promise<{ digital_twin_id: string; tags: DigitalTwinTag[]; total: number }> {
  const res = await api.get(`/tags/by-digital-twin/${digitalTwinId}`);
  return res.data;
}

export interface CreateTagPayload {
  asset_id: string;
  digital_twin_id: string;
  tag_type: "point" | "line" | "polygon" | "area";
  geometry: Record<string, unknown>;
  label?: string;
  description?: string;
  color?: string;
}

export async function createTag(payload: CreateTagPayload): Promise<DigitalTwinTag> {
  const res = await api.post("/tags", null, {
    params: {
      asset_id: payload.asset_id,
      digital_twin_id: payload.digital_twin_id,
      tag_type: payload.tag_type,
      geometry: JSON.stringify(payload.geometry),
      label: payload.label,
      description: payload.description,
      color: payload.color,
    },
  });
  return res.data;
}

/**
 * Delete a 3D tag. Removes the asset↔twin link for this tag — the asset
 * itself stays in the asset register; only the spatial linkage is dropped.
 */
export async function deleteTag(tagId: string): Promise<void> {
  await api.delete(`/tags/${tagId}`);
}

export interface UpdateTagPayload {
  /** Reassign the tag to a different asset. The new asset must be in the
   *  same virtual site, and must not already be tagged on this container. */
  asset_id?: string;
  label?: string | null;
  color?: string;
}

export async function updateTag(tagId: string, payload: UpdateTagPayload): Promise<DigitalTwinTag> {
  const res = await api.put(`/tags/${tagId}`, null, {
    params: {
      asset_id: payload.asset_id,
      label: payload.label,
      color: payload.color,
    },
  });
  return res.data;
}

/**
 * Visually distinct color palette for auto-assigning to new tags. Avoids the
 * "every tag is indigo" problem when a user places several tags in a row.
 * Colors are spaced ~60° around the hue wheel and biased toward saturated,
 * legible values against the dark splat background.
 */
const TAG_COLOR_PALETTE = [
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // amber
  "#22c55e", // green
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#a855f7", // purple
  "#ec4899", // pink
  "#10b981", // emerald
  "#f59e0b", // yellow
];

/**
 * Pick a random tag color, preferring ones not already in use on this twin.
 * Falls back to a uniformly random palette entry once all palette colors
 * are taken.
 */
export function pickRandomTagColor(usedColors: ReadonlyArray<string | null | undefined> = []): string {
  const used = new Set(
    usedColors
      .filter((c): c is string => typeof c === "string")
      .map((c) => c.toLowerCase()),
  );
  const fresh = TAG_COLOR_PALETTE.filter((c) => !used.has(c.toLowerCase()));
  const pool = fresh.length > 0 ? fresh : TAG_COLOR_PALETTE;
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Visually-distinct palette used to colour-code digital twins on the 2D map
 * and its list legend. Twins are assigned a colour by their position in the
 * (filtered) list so adjacent twins differ; the map and list share one
 * assignment so a twin's colour matches in both places.
 */
export const DIGITAL_TWIN_MAP_PALETTE = [
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // amber
  "#22c55e", // green
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#a855f7", // purple
  "#ec4899", // pink
  "#10b981", // emerald
  "#f59e0b", // yellow
];

/**
 * Compact code shown on a map tag / list chip in place of the full twin name.
 * Multi-word names → initials of the first three words ("Turbine Hall A" →
 * "THA"); single-word names → first three letters ("Warehouse" → "WAR").
 */
export function digitalTwinShortLabel(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words
    .slice(0, 3)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

/** Pick black or white text for legibility over a hex background colour. */
export function contrastText(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#111827" : "#ffffff";
}
